import { defineStore } from 'pinia';
import { API_BASE_URL } from '@/utils/getApiBase';
import { request } from '@/service/request';

export interface WorkspaceContext {
  enabled: boolean;
  state?: 'uninitialized' | 'ready';
  organization?: { id: string; name: string; is_personal: boolean } | null;
  projects?: { id: string; name: string; description: string }[];
  selected_project_id?: string | null;
  billing_owner?: 'user';
}

export function parseWorkspaceContext(value: unknown): WorkspaceContext {
  const data = value as WorkspaceContext | null;
  if (data?.enabled === false) return { enabled: false };
  if (data?.enabled !== true || data.billing_owner !== 'user' || !Array.isArray(data.projects) || data.projects.length > 50) {
    throw new Error('Invalid workspace context');
  }
  if (data.state === 'uninitialized' && data.organization === null && data.projects.length === 0 && data.selected_project_id === null) return data;
  if (data.state !== 'ready' || !data.organization || typeof data.organization.id !== 'string' || !/^org_[A-Za-z0-9]{24}$/.test(data.organization.id) ||
      typeof data.organization.name !== 'string' || data.organization.name.length > 255 || data.organization.is_personal !== true ||
      data.projects.some(project => !project || typeof project.id !== 'string' || !/^proj_[A-Za-z0-9]{24}$/.test(project.id) ||
        typeof project.name !== 'string' || project.name.length > 255 || typeof project.description !== 'string') ||
      new Set(data.projects.map(project => project.id)).size !== data.projects.length ||
      (data.projects.length === 0 ? data.selected_project_id !== null : !data.projects.some(project => project.id === data.selected_project_id))) {
    throw new Error('Invalid workspace context');
  }
  return data;
}

export const useOrgProjectsStore = defineStore('org-projects', {
  state: () => ({ context: { enabled: false } as WorkspaceContext, loading: false, failed: false, generation: 0 }),
  actions: {
    clear() { this.generation += 1; this.context = { enabled: false }; this.failed = false; this.loading = false; },
    async load() {
      const generation = ++this.generation;
      this.loading = true;
      this.failed = false;
      try {
        const { data, error } = await request<WorkspaceContext>({ url: `${API_BASE_URL}/api/data/platform/organization-projects/`, method: 'get' });
        if (error) throw new Error('Workspace unavailable');
        if (generation === this.generation) this.context = parseWorkspaceContext(data);
      } catch {
        if (generation === this.generation) this.failed = true;
      } finally { if (generation === this.generation) this.loading = false; }
    },
    async mutate(action: string, values: Record<string, string> = {}) {
      const generation = ++this.generation;
      this.loading = true;
      this.failed = false;
      try {
        const { data, error } = await request<WorkspaceContext>({
          url: `${API_BASE_URL}/api/data/platform/organization-projects/`, method: 'post', data: { action, ...values }
        });
        if (error) throw new Error('Workspace update failed');
        if (generation === this.generation) this.context = parseWorkspaceContext(data);
      } catch {
        if (generation === this.generation) { this.failed = true; throw new Error('Workspace update failed'); }
      } finally { if (generation === this.generation) this.loading = false; }
    }
  }
});
